import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { User, Users, Building, DollarSign, Lock, Upload, Sparkles, ExternalLink, Bell, ChevronDown, ChevronUp, AlertCircle, CheckCircle, AlertTriangle, Download, Trash2, Calendar, Plug } from 'lucide-react';
import NotificationPreferences from '../components/NotificationPreferences';
import TeamManagement from '../components/TeamManagement';
import { toast } from 'sonner';
import { PARTNER_LEVELS } from '../constants/projectData';
import { countries } from '../utils/countries';
import { useAutoSave } from '../contexts/AutoSaveContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Google Calendar Integration Card Component
const CalendarIntegrationCard = () => {
  const [calendarStatus, setCalendarStatus] = React.useState({ connected: false, email: null });
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    fetchCalendarStatus();
    
    // Check URL for callback status
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_calendar') === 'connected') {
      toast.success('Google Calendar connected successfully!');
      // Clear the URL parameter
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('error') === 'google_auth_failed') {
      toast.error(`Failed to connect Google Calendar: ${params.get('message') || 'Unknown error'}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchCalendarStatus = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/calendar/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendarStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch calendar status:', error);
    } finally {
      setLoading(false);
    }
  };

  const connectCalendar = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/auth/google/calendar/login`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Redirect to Google auth
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error('Failed to initiate Google Calendar connection');
    }
  };

  const disconnectCalendar = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/calendar/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendarStatus({ connected: false, email: null });
      toast.success('Google Calendar disconnected');
    } catch (error) {
      toast.error('Failed to disconnect calendar');
    }
  };

  // Microsoft Outlook integration
  const [microsoftStatus, setMicrosoftStatus] = React.useState({ connected: false, email: null });

  React.useEffect(() => {
    fetchMicrosoftStatus();
    
    // Check URL for Microsoft callback status
    const params = new URLSearchParams(window.location.search);
    if (params.get('microsoft_calendar') === 'connected') {
      toast.success('Microsoft Outlook Calendar connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
      fetchMicrosoftStatus();
    }
    if (params.get('error') === 'microsoft_auth_failed') {
      toast.error(`Failed to connect Microsoft Calendar: ${params.get('message') || 'Unknown error'}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchMicrosoftStatus = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/calendar/microsoft/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMicrosoftStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch Microsoft calendar status:', error);
    }
  };

  const connectMicrosoftCalendar = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/auth/microsoft/calendar/login`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error('Failed to initiate Microsoft Calendar connection');
    }
  };

  const disconnectMicrosoftCalendar = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/calendar/microsoft/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMicrosoftStatus({ connected: false, email: null });
      toast.success('Microsoft Outlook Calendar disconnected');
    } catch (error) {
      toast.error('Failed to disconnect Microsoft calendar');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Calendar Integration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-gray-600">
          Connect your calendar to automatically send meeting invites for interviews.
        </p>
        
        {/* Google Calendar Section */}
        <div className="border rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2">
            <svg className="w-6 h-6" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M19.5 4H17V3C17 2.4 16.6 2 16 2S15 2.4 15 3V4H9V3C9 2.4 8.6 2 8 2S7 2.4 7 3V4H4.5C3.1 4 2 5.1 2 6.5V19.5C2 20.9 3.1 22 4.5 22H19.5C20.9 22 22 20.9 22 19.5V6.5C22 5.1 20.9 4 19.5 4ZM20 19.5C20 19.8 19.8 20 19.5 20H4.5C4.2 20 4 19.8 4 19.5V9H20V19.5Z"/>
            </svg>
            <h4 className="font-semibold">Google Calendar</h4>
          </div>
          
          {loading ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : calendarStatus.connected ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm">Connected to {calendarStatus.email}</span>
              </div>
              <Button variant="outline" size="sm" onClick={disconnectCalendar}>
                Disconnect
              </Button>
            </div>
          ) : (
            <Button onClick={connectCalendar} size="sm" className="bg-blue-600 hover:bg-blue-700">
              Connect Google Calendar
            </Button>
          )}
        </div>
        
        {/* Microsoft Outlook Section */}
        <div className="border rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2">
            <svg className="w-6 h-6" viewBox="0 0 24 24">
              <path fill="#0078D4" d="M21.5,2H6.5A1.5,1.5,0,0,0,5,3.5v4l9.5,4L24,7.5V3.5A1.5,1.5,0,0,0,22.5,2Z"/>
              <path fill="#0364B8" d="M5,7.5V20.5A1.5,1.5,0,0,0,6.5,22H21.5A1.5,1.5,0,0,0,23,20.5V7.5l-9,6Z"/>
              <path fill="#0A2767" d="M1,6.5v12A1.5,1.5,0,0,0,2.5,20H5V6.5Z"/>
              <path fill="#0078D4" d="M11,6.5V18a1.5,1.5,0,0,1-1.5,1.5H2.5a1.47,1.47,0,0,1-.5-.09V6A1.5,1.5,0,0,1,3.5,4.5H9.5A1.5,1.5,0,0,1,11,6Z"/>
              <path fill="#FFFFFF" opacity="0.5" d="M10.5,7.5v9A1.5,1.5,0,0,1,9,18H3V7h7.5Z"/>
              <text x="4" y="14" fill="#0078D4" fontSize="7" fontWeight="bold">O</text>
            </svg>
            <h4 className="font-semibold">Microsoft Outlook</h4>
          </div>
          
          {microsoftStatus.connected ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm">Connected to {microsoftStatus.email}</span>
              </div>
              <Button variant="outline" size="sm" onClick={disconnectMicrosoftCalendar}>
                Disconnect
              </Button>
            </div>
          ) : (
            <Button onClick={connectMicrosoftCalendar} size="sm" className="bg-[#0078D4] hover:bg-[#106EBE]">
              Connect Outlook Calendar
            </Button>
          )}
        </div>
        
        <div className="p-3 bg-blue-50 rounded-lg">
          <p className="text-xs text-blue-700">
            <strong>How it works:</strong> When an interview is confirmed, you can send a calendar invite directly from Snow Exchange. 
            The invite will include a meeting link (Google Meet or Microsoft Teams) and will be sent to all participants.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, checkAuth } = useContext(AuthContext);
  const { setAutoSaving, setLastSaved } = useAutoSave();
  const [loading, setLoading] = useState(true);
  
  // Check if user is a freelancer
  const isFreelance = user?.is_freelance === true;
  
  // Get tab from URL query params
  const searchParams = new URLSearchParams(window.location.search);
  const initialTab = searchParams.get('tab') || 'personal';

  // Cleanup auto-save when leaving page
  useEffect(() => {
    return () => {
      setAutoSaving(false);
      setLastSaved(null);
    };
  }, [setAutoSaving, setLastSaved]);
  
  const [userProfile, setUserProfile] = useState({
    first_name: '',
    last_name: '',
    job_title: '',
    phone: '',
    linkedin: '',
    profile_photo: ''
  });

  const [companyProfile, setCompanyProfile] = useState({
    presentation: '',
    snow_focus: '',
    partner_level: 'Specialist',
    address_hq: '',
    postal_code_hq: '',
    city_hq: '',
    country_hq: '',
    address_office: '',
    postal_code_office: '',
    city_office: '',
    country_office: '',
    country: '',
    registration_number: '',
    vat_number: '',
    website: '',
    logo_url: ''
  });

  // Enhanced company fields (from Company model)
  const [enhancedCompany, setEnhancedCompany] = useState({
    name: '',
    website_url: '',
    description: '',
    focus_specialization: '',
    linkedin_url: '',
    company_type: 'partner',
    company_type_detail: '',
    csat_link: '',
    csat_score: '',
    csat_month: '',
    csat_year: '',
    partner_finder_url: '',
    logo_url: '',
    partner_level: 'Specialist'
  });

  const [sameAddress, setSameAddress] = useState(true); // Checked by default

  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [logoTimestamp, setLogoTimestamp] = useState(Date.now()); // For cache busting
  
  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    identity: false,
    focus: false,
    partner: false,
    registration: false,
    office: false,
    hq: false
  });
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Account deletion states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    const token = localStorage.getItem('token');
    try {
      const [userRes, companyRes, enhancedCompanyRes] = await Promise.all([
        axios.get(`${API}/profile/user`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/profile/company`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/companies/my_profile`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setUserProfile(userRes.data);
      setCompanyProfile(companyRes.data);
      // Parse csat_date if it exists (format: YYYY-MM)
      let csatMonth = '';
      let csatYear = '';
      if (enhancedCompanyRes.data.csat_date) {
        const [year, month] = enhancedCompanyRes.data.csat_date.split('-');
        csatYear = year || '';
        csatMonth = month || '';
      }

      setEnhancedCompany({
        name: enhancedCompanyRes.data.name || '',
        website_url: enhancedCompanyRes.data.website_url || '',
        description: enhancedCompanyRes.data.description || '',
        focus_specialization: enhancedCompanyRes.data.focus_specialization || '',
        linkedin_url: enhancedCompanyRes.data.linkedin_url || '',
        company_type: enhancedCompanyRes.data.company_type || 'partner',
        company_type_detail: enhancedCompanyRes.data.company_type_detail || '',
        csat_link: enhancedCompanyRes.data.csat_link || '',
        csat_score: enhancedCompanyRes.data.csat_score || '',
        csat_month: csatMonth,
        csat_year: csatYear,
        partner_finder_url: enhancedCompanyRes.data.partner_finder_url || '',
        logo_url: enhancedCompanyRes.data.logo_url || '',
        partner_level: companyRes.data.partner_level || 'Specialist'
      });
    } catch (error) {
      toast.error('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  // Auto-save for user profile with debounce
  useEffect(() => {
    if (loading) return; // Don't auto-save during initial load
    
    const timeoutId = setTimeout(() => {
      if (userProfile.first_name && userProfile.last_name && userProfile.phone) {
        autoSaveUserProfile();
      }
    }, 2000); // Wait 2 seconds after last change

    return () => clearTimeout(timeoutId);
  }, [userProfile.first_name, userProfile.last_name, userProfile.phone, userProfile.job_title]);

  // Auto-save for company profile with debounce
  useEffect(() => {
    if (loading) return; // Don't auto-save during initial load
    
    const timeoutId = setTimeout(() => {
      if (enhancedCompany.name) {
        autoSaveCompanyProfile();
      }
    }, 2000); // Wait 2 seconds after last change

    return () => clearTimeout(timeoutId);
  }, [enhancedCompany.name, enhancedCompany.website_url, enhancedCompany.description, enhancedCompany.focus_specialization, enhancedCompany.linkedin_url, enhancedCompany.csat_score, enhancedCompany.csat_month, enhancedCompany.csat_year, companyProfile.street_office, companyProfile.city_office, companyProfile.postal_code_office, companyProfile.country_office]);

  const autoSaveUserProfile = async () => {
    setAutoSaving(true);
    try {
      await axios.put(`${API}/profile/user`, userProfile, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setLastSaved(new Date());
    } catch (error) {
      console.error('Auto-save failed:', error);
    } finally {
      setAutoSaving(false);
    }
  };

  const autoSaveCompanyProfile = async () => {
    setAutoSaving(true);
    try {
      // Build csat_date from month and year
      let csatDate = null;
      if (enhancedCompany.csat_year && enhancedCompany.csat_month) {
        csatDate = `${enhancedCompany.csat_year}-${enhancedCompany.csat_month.padStart(2, '0')}`;
      }

      const params = {
        name: enhancedCompany.name,
        website_url: enhancedCompany.website_url,
        description: enhancedCompany.description,
        focus_specialization: enhancedCompany.focus_specialization,
        linkedin_url: enhancedCompany.linkedin_url,
        company_type: enhancedCompany.company_type,
        company_type_detail: enhancedCompany.company_type_detail,
        csat_link: enhancedCompany.csat_link,
        csat_score: enhancedCompany.csat_score ? parseFloat(enhancedCompany.csat_score) : null,
        csat_date: csatDate,
        partner_finder_url: enhancedCompany.partner_finder_url,
        // Include office address fields from companyProfile
        street_office: companyProfile.street_office || null,
        city_office: companyProfile.city_office || null,
        postal_code_office: companyProfile.postal_code_office || null,
        country_office: companyProfile.country_office || null
      };

      await axios.put(`${API}/companies/my_profile`, null, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params
      });
      setLastSaved(new Date());
    } catch (error) {
      console.error('Auto-save failed:', error);
    } finally {
      setAutoSaving(false);
    }
  };

  const updateUserProfile = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!userProfile.first_name || !userProfile.last_name) {
      toast.error('First name and last name are required');
      return;
    }
    if (!userProfile.phone) {
      toast.error('Phone number is required');
      return;
    }
    if (!userProfile.linkedin) {
      toast.error('LinkedIn profile is required');
      return;
    }
    
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/profile/user`, userProfile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated successfully');
      checkAuth();
      // Notify sidebar to refresh profile completeness
      window.dispatchEvent(new CustomEvent('profileUpdated', { 
        detail: { timestamp: Date.now() } 
      }));
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const updateCompanyProfile = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/profile/company`, companyProfile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Company profile updated successfully');
      // Notify sidebar to refresh profile completeness
      window.dispatchEvent(new CustomEvent('profileUpdated', { 
        detail: { timestamp: Date.now() } 
      }));
      window.dispatchEvent(new CustomEvent('companyInfoUpdated', { 
        detail: { timestamp: Date.now() } 
      }));
    } catch (error) {
      toast.error('Failed to update company profile');
    }
  };

  const updateEnhancedCompany = async (e) => {
    e.preventDefault();
    
    // Validate mandatory fields
    if (!enhancedCompany.website_url) {
      toast.error('Website URL is required');
      return;
    }
    if (!enhancedCompany.description) {
      toast.error('Company description is required');
      return;
    }
    if (!enhancedCompany.focus_specialization) {
      toast.error('Focus/Specialization is required');
      return;
    }
    
    const token = localStorage.getItem('token');
    try {
      // Build csat_date from month and year
      let csatDate = null;
      if (enhancedCompany.csat_year && enhancedCompany.csat_month) {
        csatDate = `${enhancedCompany.csat_year}-${enhancedCompany.csat_month.padStart(2, '0')}`;
      }

      const params = {
        name: enhancedCompany.name,
        website_url: enhancedCompany.website_url,
        description: enhancedCompany.description,
        focus_specialization: enhancedCompany.focus_specialization,
        linkedin_url: enhancedCompany.linkedin_url,
        company_type: enhancedCompany.company_type,
        company_type_detail: enhancedCompany.company_type_detail,
        csat_link: enhancedCompany.csat_link,
        csat_score: enhancedCompany.csat_score ? parseFloat(enhancedCompany.csat_score) : null,
        csat_date: csatDate,
        partner_finder_url: enhancedCompany.partner_finder_url
      };
      
      // Also update partner_level in legacy profile
      const legacyParams = {
        ...companyProfile,
        partner_level: enhancedCompany.partner_level
      };
      
      await Promise.all([
        axios.put(`${API}/companies/my_profile`, null, {
          headers: { Authorization: `Bearer ${token}` },
          params
        }),
        axios.put(`${API}/profile/company`, legacyParams, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      toast.success('Company profile updated successfully');
      fetchProfiles(); // Refresh data
      // Notify sidebar to refresh profile completeness
      window.dispatchEvent(new CustomEvent('profileUpdated', { 
        detail: { timestamp: Date.now() } 
      }));
      window.dispatchEvent(new CustomEvent('companyInfoUpdated', { 
        detail: { timestamp: Date.now() } 
      }));
    } catch (error) {
      toast.error('Failed to update enhanced company profile');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, or WebP image.');
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size exceeds 5MB limit');
      return;
    }
    
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    
    setUploadingLogo(true);
    try {
      const response = await axios.post(`${API}/companies/my_profile/upload_logo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Update local state with new logo URL
      setEnhancedCompany({ ...enhancedCompany, logo_url: response.data.logo_url });
      
      // Update timestamp to force image refresh (cache busting)
      setLogoTimestamp(Date.now());
      
      toast.success('Logo uploaded successfully');
      
      // Notify layout to refresh company info
      window.dispatchEvent(new CustomEvent('companyInfoUpdated', { 
        detail: { timestamp: Date.now() } 
      }));
      
      // Force re-fetch profiles to get fresh data
      await fetchProfiles();
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const generateAIDescription = async () => {
    const token = localStorage.getItem('token');
    setGeneratingDescription(true);
    
    try {
      const params = {
        company_name: enhancedCompany.name,
        focus: enhancedCompany.focus_specialization,
        website_url: enhancedCompany.website_url,
        linkedin_url: enhancedCompany.linkedin_url
      };
      
      const response = await axios.post(`${API}/companies/my_profile/generate_description_ai`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      if (response.data.success) {
        setEnhancedCompany({ ...enhancedCompany, description: response.data.description });
        toast.success('AI description generated based on your website and LinkedIn profile');
      }
    } catch (error) {
      toast.error('Failed to generate AI description. Please try again.');
    } finally {
      setGeneratingDescription(false);
    }
  };

  const handleSameAddressChange = (checked) => {
    setSameAddress(checked);
    if (checked) {
      setCompanyProfile({
        ...companyProfile,
        address_hq: companyProfile.address_office,
        postal_code_hq: companyProfile.postal_code_office,
        city_hq: companyProfile.city_office,
        country_hq: companyProfile.country_office
      });
    }
  };


  // Check if sections are complete (for red dot indicators)
  const isSectionComplete = (section) => {
    switch(section) {
      case 'identity':
        return enhancedCompany.name && enhancedCompany.website_url && enhancedCompany.description;
      case 'focus':
        return enhancedCompany.focus_specialization;
      case 'partner':
        return companyProfile.partner_level;
      case 'registration':
        return companyProfile.registration_number && companyProfile.country_hq;
      case 'office':
        return companyProfile.address_office && companyProfile.city_office && companyProfile.country_office;
      case 'hq':
        return companyProfile.address_hq && companyProfile.city_hq && companyProfile.country_hq;
      default:
        return true;
    }
  };


  const handleExportData = async () => {
    setIsExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/export-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Download as JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `snow-exchange-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Data exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  const handleRequestDeletion = async () => {
    if (deleteConfirmText !== 'DELETE MY ACCOUNT') {
      toast.error('Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }

    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/request-deletion`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Deletion request confirmed. Check your email.');
      setShowDeleteModal(false);
      checkAuth(); // Refresh user data
    } catch (error) {
      console.error('Deletion request error:', error);
      toast.error(error.response?.data?.detail || 'Failed to request deletion');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDeletion = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/cancel-deletion`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Deletion cancelled. Your account is safe!');
      checkAuth(); // Refresh user data
    } catch (error) {
      console.error('Cancel deletion error:', error);
      toast.error('Failed to cancel deletion');
    }
  };

  const updatePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    // Password update logic would go here
    toast.info('Password update feature coming soon');
    setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
  };
  
  // Check if required personal fields are missing
  const hasPersonalIssues = () => {
    return !userProfile.first_name || !userProfile.last_name || !userProfile.phone || !userProfile.linkedin;
  };
  
  // Check if required company fields are missing
  const hasCompanyIssues = () => {
    return !enhancedCompany.name || !enhancedCompany.website_url || !enhancedCompany.description || !enhancedCompany.focus_specialization;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex justify-center items-center h-96">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="profile-page">

      <main className="max-w-5xl mx-auto px-6 py-8 ">
        <div className="mb-8 fade-in">
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#1B3240' }}>
            Account Settings
          </h1>
          <p className="text-lg text-gray-600">
            Manage your personal and company information
          </p>
        </div>

        <Tabs defaultValue={initialTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-7 mb-6">
            <TabsTrigger value="personal" className="relative">
              <User className="w-4 h-4 mr-2" />
              Personal
              {hasPersonalIssues() && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full" title="Required information missing"></span>
              )}
            </TabsTrigger>
            <TabsTrigger value="company" className="relative">
              <Building className="w-4 h-4 mr-2" />
              Company
              {hasCompanyIssues() && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full" title="Required information missing"></span>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="team" 
              disabled={isFreelance}
              className={isFreelance ? 'opacity-50 cursor-not-allowed' : ''}
              title={isFreelance ? 'Team management is not available for freelancers' : ''}
            >
              <Users className="w-4 h-4 mr-2" />
              Team
              {isFreelance && <Lock className="w-3 h-3 ml-1 text-gray-400" />}
            </TabsTrigger>
            <TabsTrigger value="pricing">
              <DollarSign className="w-4 h-4 mr-2" />
              Pricing
            </TabsTrigger>
            <TabsTrigger value="notifications">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="integrations">
              <Plug className="w-4 h-4 mr-2" />
              Integrations
            </TabsTrigger>
            <TabsTrigger value="security">
              <Lock className="w-4 h-4 mr-2" />
              Security
            </TabsTrigger>
          </TabsList>

          {/* Personal Info Tab */}
          <TabsContent value="personal">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle style={{ color: '#1B3240' }}>Personal Information</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={updateUserProfile} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="first_name">
                        First Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="first_name"
                        value={userProfile.first_name || ''}
                        onChange={(e) => setUserProfile({ ...userProfile, first_name: e.target.value })}
                        placeholder="John"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="last_name">
                        Last Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="last_name"
                        value={userProfile.last_name || ''}
                        onChange={(e) => setUserProfile({ ...userProfile, last_name: e.target.value })}
                        placeholder="Doe"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="job_title">Job Title</Label>
                    <Input
                      id="job_title"
                      value={userProfile.job_title || ''}
                      onChange={(e) => setUserProfile({ ...userProfile, job_title: e.target.value })}
                      placeholder="e.g., ServiceNow Consultant, Project Manager"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="bg-gray-100"
                    />
                    <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                  </div>

                  <div>
                    <Label htmlFor="phone">
                      Phone Number <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={userProfile.phone || ''}
                      onChange={(e) => setUserProfile({ ...userProfile, phone: e.target.value })}
                      placeholder="+33 1 23 45 67 89"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="linkedin">
                      LinkedIn Profile <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="linkedin"
                      value={userProfile.linkedin || ''}
                      onChange={(e) => setUserProfile({ ...userProfile, linkedin: e.target.value })}
                      placeholder="https://linkedin.com/in/yourprofile"
                      required
                    />
                  </div>

                  <div className="pt-4">
                    <Button type="submit" className="btn-snow">
                      Save Personal Info
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Company Info Tab */}
          <TabsContent value="company">
            <div className="space-y-6">
              {/* View Public Profile Button */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-1">Public Company Profile</h3>
                      <p className="text-sm text-blue-700">See how other companies view your profile</p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/company/${user?.company_id}`)}
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Public Profile
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Company Profile Card */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle style={{ color: '#1B3240' }}>Company Profile</CardTitle>
                  <p className="text-sm text-gray-500">Essential company information</p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={updateEnhancedCompany} className="space-y-6">
                    
                    {/* Logo Upload Section */}
                    <div className="border-b pb-6">
                      <Label className="text-base font-semibold mb-3 block">Company Logo</Label>
                      <div className="flex items-center gap-6">
                        {enhancedCompany.logo_url ? (
                          <div className="relative">
                            <img 
                              src={`${BACKEND_URL}${enhancedCompany.logo_url}?t=${logoTimestamp}`} 
                              alt="Company Logo" 
                              className="w-24 h-24 object-contain border rounded-lg"
                              key={logoTimestamp}
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="absolute -top-2 -right-2"
                              onClick={() => document.getElementById('logo-upload').click()}
                            >
                              Change
                            </Button>
                          </div>
                        ) : (
                          <div className="w-24 h-24 border-2 border-dashed rounded-lg flex items-center justify-center bg-gray-50">
                            <Building className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1">
                          <input
                            id="logo-upload"
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            onChange={handleLogoUpload}
                            className="hidden"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => document.getElementById('logo-upload').click()}
                            disabled={uploadingLogo}
                          >
                            {uploadingLogo ? (
                              <>Uploading...</>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                Upload Logo
                              </>
                            )}
                          </Button>
                          <p className="text-xs text-gray-500 mt-2">
                            Supported formats: JPEG, PNG, WebP (Max 5MB)
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Company Identity - Collapsible */}
                    <div className="space-y-4">
                      <div 
                        className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded -m-2"
                        onClick={() => toggleSection('identity')}
                      >
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-semibold text-gray-700">Company Identity</h3>
                          {!isSectionComplete('identity') && (
                            <span className="w-2 h-2 bg-red-500 rounded-full" title="Required information missing"></span>
                          )}
                        </div>
                        {expandedSections.identity ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                      
                      {expandedSections.identity && (
                        <div className="space-y-4 pt-2">
                          <div>
                            <Label htmlFor="company_name">
                              Company Name <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              id="company_name"
                              value={enhancedCompany.name}
                              onChange={(e) => setEnhancedCompany({ ...enhancedCompany, name: e.target.value })}
                              placeholder="Your Company Name"
                              required
                            />
                          </div>

                          <div>
                            <Label htmlFor="website_url">
                              Website URL <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              id="website_url"
                              type="url"
                              value={enhancedCompany.website_url}
                              onChange={(e) => setEnhancedCompany({ ...enhancedCompany, website_url: e.target.value })}
                              placeholder="https://yourcompany.com"
                              required
                            />
                          </div>

                          <div>
                            <Label htmlFor="linkedin_url">
                              LinkedIn Company Page
                            </Label>
                            <div className="relative">
                              <Input
                                id="linkedin_url"
                                type="url"
                                value={enhancedCompany.linkedin_url}
                                onChange={(e) => setEnhancedCompany({ ...enhancedCompany, linkedin_url: e.target.value })}
                                placeholder="https://linkedin.com/company/yourcompany"
                              />
                              {enhancedCompany.linkedin_url && (
                                <a 
                                  href={enhancedCompany.linkedin_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="absolute right-3 top-3 text-blue-600 hover:text-blue-700"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              )}
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="company_type">
                              Company Type <span className="text-red-500">*</span>
                            </Label>
                            <select
                              id="company_type"
                              className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isFreelance ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                              value={isFreelance ? 'freelance' : enhancedCompany.company_type}
                              onChange={(e) => !isFreelance && setEnhancedCompany({ ...enhancedCompany, company_type: e.target.value })}
                              disabled={isFreelance}
                              required
                            >
                              <option value="partner">ServiceNow Partner</option>
                              <option value="consulting">Consulting Firm</option>
                              <option value="recruitment">Recruitment Agency</option>
                              <option value="servicenow_editor">ServiceNow Editor/ISV</option>
                              <option value="freelance">Freelance / Independent Consultant</option>
                              <option value="end_client">End Client (ServiceNow User)</option>
                              <option value="other">Other</option>
                            </select>
                            {isFreelance && (
                              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                <Lock className="w-3 h-3" />
                                Locked for freelance accounts
                              </p>
                            )}
                          </div>

                          {enhancedCompany.company_type === 'other' && (
                            <div>
                              <Label htmlFor="company_type_detail">Please specify</Label>
                              <Input
                                id="company_type_detail"
                                value={enhancedCompany.company_type_detail}
                                onChange={(e) => setEnhancedCompany({ ...enhancedCompany, company_type_detail: e.target.value })}
                                placeholder="Describe your company type"
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Business Focus - Collapsible */}
                    <div className="border-t pt-6 space-y-4">
                      <div 
                        className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded -m-2"
                        onClick={() => toggleSection('focus')}
                      >
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-semibold text-gray-700">Business Focus</h3>
                          {!isSectionComplete('focus') && (
                            <span className="w-2 h-2 bg-red-500 rounded-full" title="Required information missing"></span>
                          )}
                        </div>
                        {expandedSections.focus ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                      
                      {expandedSections.focus && (
                        <div className="space-y-4 pt-2">
                          <div>
                            <Label htmlFor="focus_specialization">
                              Focus / Specialization <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              id="focus_specialization"
                              value={enhancedCompany.focus_specialization}
                              onChange={(e) => setEnhancedCompany({ ...enhancedCompany, focus_specialization: e.target.value })}
                              placeholder="e.g., ITSM, ITOM, CSM implementations"
                              required
                            />
                          </div>

                          <div>
                            <Label htmlFor="description">
                              Company Description <span className="text-red-500">*</span>
                            </Label>
                            <div className="space-y-2">
                              <Textarea
                                id="description"
                                value={enhancedCompany.description}
                                onChange={(e) => setEnhancedCompany({ ...enhancedCompany, description: e.target.value })}
                                rows={6}
                                placeholder="Describe your company, expertise, and value proposition..."
                                required
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={generateAIDescription}
                                disabled={generatingDescription}
                              >
                                {generatingDescription ? (
                                  <>Generating...</>
                                ) : (
                                  <>
                                    <Sparkles className="w-4 h-4 mr-2" />
                                    Generate with AI
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* ServiceNow Partner Information - Conditional & Collapsible (not shown for freelancers) */}
                    {!isFreelance && enhancedCompany.company_type === 'partner' && (
                      <div className="border-t pt-6 space-y-4">
                        <div 
                          className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded -m-2"
                          onClick={() => toggleSection('partner')}
                        >
                          <div className="flex items-center gap-2">
                            <h3 className="text-base font-semibold text-gray-700">ServiceNow Partner Information</h3>
                            {!isSectionComplete('partner') && (
                              <span className="w-2 h-2 bg-red-500 rounded-full" title="Required information missing"></span>
                            )}
                          </div>
                          {expandedSections.partner ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </div>
                        
                        {expandedSections.partner && (
                          <div className="space-y-4 pt-2">
                            <div>
                              <Label htmlFor="partner_level">Partner Level</Label>
                              <select
                                id="partner_level"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                value={enhancedCompany.partner_level}
                                onChange={(e) => setEnhancedCompany({ ...enhancedCompany, partner_level: e.target.value })}
                              >
                                {PARTNER_LEVELS.map(level => (
                                  <option key={level} value={level}>{level}</option>
                                ))}
                              </select>
                            </div>

                            <div className="grid md:grid-cols-3 gap-4">
                              <div>
                                <Label htmlFor="csat_score">ServiceNow CSAT Score</Label>
                                <Input
                                  id="csat_score"
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max="5"
                                  value={enhancedCompany.csat_score}
                                  onChange={(e) => setEnhancedCompany({ ...enhancedCompany, csat_score: e.target.value })}
                                  placeholder="4.65"
                                />
                                <p className="text-xs text-gray-500 mt-1">Score out of 5 (e.g., 4.65)</p>
                              </div>
                              <div>
                                <Label htmlFor="csat_month">CSAT Month</Label>
                                <select
                                  id="csat_month"
                                  value={enhancedCompany.csat_month}
                                  onChange={(e) => setEnhancedCompany({ ...enhancedCompany, csat_month: e.target.value })}
                                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                >
                                  <option value="">Select month...</option>
                                  <option value="01">January</option>
                                  <option value="02">February</option>
                                  <option value="03">March</option>
                                  <option value="04">April</option>
                                  <option value="05">May</option>
                                  <option value="06">June</option>
                                  <option value="07">July</option>
                                  <option value="08">August</option>
                                  <option value="09">September</option>
                                  <option value="10">October</option>
                                  <option value="11">November</option>
                                  <option value="12">December</option>
                                </select>
                                <p className="text-xs text-gray-500 mt-1">Month obtained</p>
                              </div>
                              <div>
                                <Label htmlFor="csat_year">CSAT Year</Label>
                                <Input
                                  id="csat_year"
                                  type="number"
                                  min="2020"
                                  max="2030"
                                  value={enhancedCompany.csat_year}
                                  onChange={(e) => setEnhancedCompany({ ...enhancedCompany, csat_year: e.target.value })}
                                  placeholder="2025"
                                />
                                <p className="text-xs text-gray-500 mt-1">Year obtained</p>
                              </div>
                            </div>

                            <div>
                              <Label htmlFor="partner_finder_url">ServiceNow Partner Finder URL</Label>
                              <Input
                                id="partner_finder_url"
                                type="url"
                                value={enhancedCompany.partner_finder_url}
                                onChange={(e) => setEnhancedCompany({ ...enhancedCompany, partner_finder_url: e.target.value })}
                                placeholder="https://..."
                              />
                              <p className="text-xs text-gray-500 mt-1">Your profile on ServiceNow Partner Finder</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="pt-4">
                      <Button type="submit" className="btn-snow">
                        Save Company Profile
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>

              {/* HQ & Office Information */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle style={{ color: '#1B3240' }}>Office & Headquarters Locations</CardTitle>
                  <p className="text-sm text-gray-500">Physical locations and registration details</p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={updateCompanyProfile} className="space-y-6">
                    
                    {/* Registration Information - Collapsible */}
                    <div className="space-y-4 pb-6 border-b">
                      <div 
                        className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded -m-2"
                        onClick={() => toggleSection('registration')}
                      >
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-semibold text-gray-700">Company Registration</h3>
                          {!isSectionComplete('registration') && (
                            <span className="w-2 h-2 bg-red-500 rounded-full" title="Required information missing"></span>
                          )}
                        </div>
                        {expandedSections.registration ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                      
                      {expandedSections.registration && (
                        <div className="grid md:grid-cols-2 gap-4 pt-2">
                          <div>
                            <Label htmlFor="registration_number">Company Registration Number</Label>
                            <Input
                              id="registration_number"
                              value={companyProfile.registration_number || ''}
                              onChange={(e) => setCompanyProfile({ ...companyProfile, registration_number: e.target.value })}
                              placeholder="e.g., SIRET, Company House number"
                            />
                            <p className="text-xs text-gray-500 mt-1">Official company registration number</p>
                          </div>
                          <div>
                            <Label htmlFor="vat_number">VAT Number</Label>
                            <Input
                              id="vat_number"
                              value={companyProfile.vat_number || ''}
                              onChange={(e) => setCompanyProfile({ ...companyProfile, vat_number: e.target.value })}
                              placeholder="e.g., FR12345678901"
                            />
                            <p className="text-xs text-gray-500 mt-1">Tax identification number</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Office Address (Primary) - Collapsible */}
                    <div className="space-y-4">
                      <div 
                        className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded -m-2"
                        onClick={() => toggleSection('office')}
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-base font-semibold text-gray-700">Office Address</h3>
                            {!isSectionComplete('office') && (
                              <span className="w-2 h-2 bg-red-500 rounded-full" title="Required information missing"></span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">Main office where your team operates</p>
                        </div>
                        {expandedSections.office ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                      
                      {expandedSections.office && (
                        <div className="space-y-4 pt-2">
                      
                      <div>
                        <Label htmlFor="address_office">Street Address</Label>
                        <Input
                          id="address_office"
                          value={companyProfile.address_office || ''}
                          onChange={(e) => {
                            setCompanyProfile({ ...companyProfile, address_office: e.target.value });
                            if (sameAddress) {
                              setCompanyProfile({ 
                                ...companyProfile, 
                                address_office: e.target.value, 
                                address_hq: e.target.value 
                              });
                            }
                          }}
                          placeholder="123 Office Street, Building A"
                        />
                      </div>

                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="postal_code_office">Postal Code</Label>
                          <Input
                            id="postal_code_office"
                            value={companyProfile.postal_code_office || ''}
                            onChange={(e) => {
                              setCompanyProfile({ ...companyProfile, postal_code_office: e.target.value });
                              if (sameAddress) {
                                setCompanyProfile({ 
                                  ...companyProfile, 
                                  postal_code_office: e.target.value,
                                  postal_code_hq: e.target.value 
                                });
                              }
                            }}
                            placeholder="75001"
                          />
                        </div>
                        <div>
                          <Label htmlFor="city_office">City</Label>
                          <Input
                            id="city_office"
                            value={companyProfile.city_office || ''}
                            onChange={(e) => {
                              setCompanyProfile({ ...companyProfile, city_office: e.target.value });
                              if (sameAddress) {
                                setCompanyProfile({ 
                                  ...companyProfile, 
                                  city_office: e.target.value,
                                  city_hq: e.target.value 
                                });
                              }
                            }}
                            placeholder="Paris"
                          />
                        </div>
                        <div>
                          <Label htmlFor="country_office">Country *</Label>
                          <select
                            id="country_office"
                            value={companyProfile.country_office || ''}
                            onChange={(e) => {
                              setCompanyProfile({ ...companyProfile, country_office: e.target.value });
                              if (sameAddress) {
                                setCompanyProfile({ 
                                  ...companyProfile, 
                                  country_office: e.target.value,
                                  country_hq: e.target.value 
                                });
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          >
                            <option value="">Select country</option>
                            {countries.map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                        </div>
                      )}
                    </div>

                    {/* Same Address Checkbox */}
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="same_address"
                        checked={sameAddress}
                        onChange={(e) => handleSameAddressChange(e.target.checked)}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="same_address" className="cursor-pointer">
                        Headquarters address is the same as office address
                      </Label>
                    </div>

                    {/* HQ Address (Conditional) - Collapsible */}
                    {!sameAddress && (
                      <div className="space-y-4 pt-6 border-t">
                        <div 
                          className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded -m-2"
                          onClick={() => toggleSection('hq')}
                        >
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-base font-semibold text-gray-700">Headquarters Address</h3>
                              {!isSectionComplete('hq') && (
                                <span className="w-2 h-2 bg-red-500 rounded-full" title="Required information missing"></span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">Legal/registered headquarters (if different from office)</p>
                          </div>
                          {expandedSections.hq ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </div>
                        
                        {expandedSections.hq && (
                          <div className="space-y-4 pt-2">
                        
                        <div>
                          <Label htmlFor="address_hq">Street Address</Label>
                          <Input
                            id="address_hq"
                            value={companyProfile.address_hq || ''}
                            onChange={(e) => setCompanyProfile({ ...companyProfile, address_hq: e.target.value })}
                            placeholder="123 HQ Street, Building B"
                          />
                        </div>

                        <div className="grid md:grid-cols-3 gap-4">
                          <div>
                            <Label htmlFor="postal_code_hq">Postal Code</Label>
                            <Input
                              id="postal_code_hq"
                              value={companyProfile.postal_code_hq || ''}
                              onChange={(e) => setCompanyProfile({ ...companyProfile, postal_code_hq: e.target.value })}
                              placeholder="75002"
                            />
                          </div>
                          <div>
                            <Label htmlFor="city_hq">City</Label>
                            <Input
                              id="city_hq"
                              value={companyProfile.city_hq || ''}
                              onChange={(e) => setCompanyProfile({ ...companyProfile, city_hq: e.target.value })}
                              placeholder="Paris"
                            />
                          </div>
                          <div>
                            <Label htmlFor="country_hq">Country of Registration *</Label>
                            <select
                              id="country_hq"
                              value={companyProfile.country_hq || ''}
                              onChange={(e) => setCompanyProfile({ ...companyProfile, country_hq: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            >
                              <option value="">Select country</option>
                              {countries.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                              Country where company is legally registered
                            </p>
                          </div>
                        </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="pt-4">
                      <Button type="submit" className="btn-snow">
                        Save Location Info
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team">
            <TeamManagement />
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle style={{ color: '#1B3240' }}>Subscription & Pricing</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Current Plan */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-100">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">Current Plan</h3>
                        <p className="text-sm text-gray-600">Your active subscription details</p>
                      </div>
                      <div className="text-right">
                        <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                          user?.subscription_status === 'active' ? 'bg-green-100 text-green-700' :
                          user?.subscription_status === 'trial' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {user?.subscription_status === 'active' ? '✓ Active' :
                           user?.subscription_status === 'trial' ? '⏱ Trial' :
                           user?.subscription_status?.toUpperCase() || 'No Plan'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-white rounded-lg p-4 border">
                        <p className="text-sm text-gray-500">Plan Type</p>
                        <p className="text-xl font-bold capitalize" style={{ color: '#1B3240' }}>
                          {user?.subscription_tier || 'Starter'}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-4 border">
                        <p className="text-sm text-gray-500">Status</p>
                        <p className="text-xl font-bold capitalize" style={{ color: user?.subscription_status === 'active' ? '#62D84E' : '#F59E0B' }}>
                          {user?.subscription_status === 'trial' ? 'Free Trial' : user?.subscription_status || 'Not Subscribed'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Activation Quota */}
                    {user && (
                      <div className="mt-4 bg-white rounded-lg p-4 border">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm text-gray-500">
                            {user?.role === 'resource_manager' || user?.role === 'operating_partner' ? 'Resource Activations' : 'Project Activations'}
                          </p>
                          <p className="text-sm text-gray-600">
                            Limit: {user?.subscription_tier === 'professional' ? 'Unlimited' : 
                                   user?.subscription_tier === 'advanced' ? '10' : '1'}
                          </p>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="h-3 rounded-full transition-all"
                            style={{
                              width: user?.subscription_tier === 'professional' ? '30%' :
                                     `${Math.min(100, (user?.activated_count || 0) / (user?.subscription_tier === 'advanced' ? 10 : 1) * 100)}%`,
                              backgroundColor: '#62D84E'
                            }}
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {user?.activated_count || 0} active / {
                            user?.subscription_tier === 'professional' ? '∞' : 
                            user?.subscription_tier === 'advanced' ? '10' : '1'
                          } allowed
                        </p>
                      </div>
                    )}

                    {user?.subscription_status === 'trial' && (
                      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                          <AlertTriangle className="inline w-4 h-4 mr-1" />
                          Your free trial allows limited activations. Upgrade to unlock more capacity!
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <Button 
                    className="btn-snow w-full sm:w-auto"
                    onClick={() => navigate('/pricing')}
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    View All Plans & Upgrade
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <NotificationPreferences />
          </TabsContent>

          {/* Integrations Tab - NEW */}
          <TabsContent value="integrations" className="space-y-6">
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Integrations</h2>
              <p className="text-gray-600">Connect your favorite tools to enhance your Snow Exchange experience.</p>
            </div>
            
            {/* Calendar Integration */}
            <CalendarIntegrationCard />
            
            {/* Future Integrations Placeholder */}
            <Card className="bg-gradient-to-br from-gray-50 to-slate-50 border-dashed border-2 border-gray-300">
              <CardContent className="py-8">
                <div className="text-center">
                  <Plug className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <h3 className="text-lg font-medium text-gray-700 mb-2">More Integrations Coming Soon</h3>
                  <p className="text-sm text-gray-500 max-w-md mx-auto">
                    We&apos;re working on integrations with popular tools like Slack, Microsoft Teams, Jira, and ServiceNow ITSM. Stay tuned!
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="space-y-6">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle style={{ color: '#1B3240' }}>Change Password</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={updatePassword} className="space-y-4">
                  <div>
                    <Label htmlFor="current_password">Current Password</Label>
                    <Input
                      id="current_password"
                      type="password"
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                      placeholder="••••••••"
                    />
                  </div>

                  <div>
                    <Label htmlFor="new_password">New Password</Label>
                    <Input
                      id="new_password"
                      type="password"
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                      placeholder="••••••••"
                    />
                  </div>

                  <div>
                    <Label htmlFor="confirm_password">Confirm New Password</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                      placeholder="••••••••"
                    />
                  </div>

                  <div className="pt-4">
                    <Button type="submit" className="btn-snow">
                      Update Password
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Deletion Warning Banner */}
            {user?.deletion_scheduled_at && (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-red-800 mb-2">
                        Account Deletion Scheduled
                      </h3>
                      <p className="text-red-700 mb-4">
                        Your account will be permanently deleted on{' '}
                        <strong>
                          {new Date(user.deletion_scheduled_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </strong>
                      </p>
                      <Button
                        onClick={handleCancelDeletion}
                        style={{ backgroundColor: '#62D84E' }}
                      >
                        Cancel Deletion
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Data Export */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  Export Your Data
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Download a copy of all your data stored on Snow Exchange (GDPR compliant).
                </p>
                <Button
                  onClick={handleExportData}
                  variant="outline"
                  disabled={isExporting}
                >
                  {isExporting ? 'Exporting...' : 'Export Data (JSON)'}
                </Button>
              </CardContent>
            </Card>

            {/* Account Deletion */}
            {!user?.deletion_scheduled_at && (
              <Card className="border-red-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <Trash2 className="w-5 h-5" />
                    Delete Account
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <p className="text-gray-600">
                      Permanently delete your Snow Exchange account and all associated data.
                    </p>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <h4 className="font-semibold text-yellow-800 mb-2">Before you delete:</h4>
                      <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                        <li>You have 7 days to cancel the deletion</li>
                        <li>After 7 days, all data will be permanently deleted</li>
                        <li>This action cannot be undone after the grace period</li>
                        <li>Consider exporting your data first</li>
                      </ul>
                    </div>
                    <Button
                      onClick={() => setShowDeleteModal(true)}
                      variant="destructive"
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Delete My Account
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <Card className="max-w-md w-full">
                  <CardHeader>
                    <CardTitle className="text-red-700 flex items-center gap-2">
                      <AlertTriangle className="w-6 h-6" />
                      Confirm Account Deletion
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-gray-700">
                      This will start a 7-day grace period. Your account will be permanently deleted after this period.
                    </p>
                    <div>
                      <Label htmlFor="confirm-delete">
                        Type <strong>DELETE MY ACCOUNT</strong> to confirm:
                      </Label>
                      <Input
                        id="confirm-delete"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        placeholder="DELETE MY ACCOUNT"
                        className="mt-2"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={handleRequestDeletion}
                        disabled={deleteConfirmText !== 'DELETE MY ACCOUNT' || isDeleting}
                        className="flex-1 bg-red-600 hover:bg-red-700"
                      >
                        {isDeleting ? 'Processing...' : 'Confirm Deletion'}
                      </Button>
                      <Button
                        onClick={() => {
                          setShowDeleteModal(false);
                          setDeleteConfirmText('');
                        }}
                        variant="outline"
                        disabled={isDeleting}
                      >
                        Cancel
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ProfilePage;
